/**
 * ContinuityReviewDrawer — User-facing Full Novel Review panel.
 *
 * Features:
 * - Trigger a full sequentially-structured continuity walk
 * - Pacing trend sparkline across all chapters
 * - Per-chapter collapsible findings with checkboxes for batch pacing fix
 * - Batch Pacing Fix: select flagged chapters → Claude rewrites them all
 *
 * Gated by `full_novel_review` capability (Publisher tier and above).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Lock,
  Loader2,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  TrendingUp,
  Wand2,
  Zap,
  Coins,
} from 'lucide-react';
import { userContinuityApi } from '@/lib/api';
import { useCapabilities } from '@/lib/useCapabilities';

// ── Session-level cache savings + per-call history ─────────────────────────
// Subscribes to cr-cache-status events. Resets when the drawer is closed.
// `history` is the last ≤10 events: [{type:'hit'|'write', tokens, saved}]
function useCacheSavings(drawerOpen) {
  const [sessionSaved, setSessionSaved] = useState(0.0);
  const [hitCount, setHitCount]         = useState(0);
  const [history, setHistory]           = useState([]); // last 10 events

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const handler = (e) => {
      const { saved_usd = 0, read = 0, write = 0 } = e.detail || {};
      const type   = read  > 0 ? 'hit' : 'write';
      const tokens = read  > 0 ? read  : write;
      if (saved_usd > 0) {
        setSessionSaved((prev) => parseFloat((prev + saved_usd).toFixed(5)));
        if (read > 0) setHitCount((prev) => prev + 1);
      }
      setHistory((prev) => [...prev.slice(-9), { type, tokens, saved: saved_usd }]);
    };
    window.addEventListener('cr-cache-status', handler);
    return () => window.removeEventListener('cr-cache-status', handler);
  }, [drawerOpen]);

  // Reset when drawer re-opens
  useEffect(() => {
    if (drawerOpen) { setSessionSaved(0); setHitCount(0); setHistory([]); }
  }, [drawerOpen]);

  return { sessionSaved, hitCount, history };
}

// ── Cache history mini bar chart ────────────────────────────────────────────
// Shows the last ≤10 generations as bars: green = cache hit, amber = cache write.
// Bar height encodes relative token volume; tooltip shows exact values.
function CacheHistoryChart({ history }) {
  if (!history || history.length < 2) return null;

  const maxTokens = Math.max(...history.map((h) => h.tokens || 1), 1);
  const hits  = history.filter((h) => h.type === 'hit').length;
  const total = history.length;
  const hitPct = Math.round((hits / total) * 100);

  return (
    <div
      className="rounded-md border border-border/60 overflow-hidden"
      data-testid="cache-history-chart"
    >
      <div className="flex items-center justify-between border-b border-border/40 bg-muted/20 px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <Zap className="h-3 w-3 text-muted-foreground" />
          <span className="text-[13px] font-semibold">Cache History</span>
          <span className="text-[9px] text-muted-foreground">last {total} gen{total !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[13px] text-emerald-600">
            <span className="h-2 w-2 rounded-sm bg-emerald-400 inline-block" />
            {hits} hit{hits !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1 text-[13px] text-amber-600">
            <span className="h-2 w-2 rounded-sm bg-amber-400 inline-block" />
            {total - hits} write{(total - hits) !== 1 ? 's' : ''}
          </span>
          <span
            className={`text-[13px] font-bold tabular-nums ${hitPct >= 60 ? 'text-emerald-600' : hitPct >= 30 ? 'text-amber-600' : 'text-rose-600'}`}
          >
            {hitPct}% hit
          </span>
        </div>
      </div>

      {/* Bar chart */}
      <div className="flex items-end gap-1 px-3 py-2 bg-background" style={{ height: 56 }}>
        {history.map((ev, i) => {
          const heightPct = Math.max(8, Math.round((ev.tokens / maxTokens) * 100));
          const isHit     = ev.type === 'hit';
          const bg        = isHit ? 'bg-emerald-400' : 'bg-amber-400';
          const tip       = isHit
            ? `Hit · ${Math.round(ev.tokens / 1000)}k tokens · $${ev.saved.toFixed(4)} saved`
            : `Write · ${Math.round(ev.tokens / 1000)}k tokens (warming cache)`;
          return (
            <div
              key={i}
              title={tip}
              className={`flex-1 min-w-[6px] rounded-t-sm ${bg} opacity-80 hover:opacity-100 transition-opacity cursor-default`}
              style={{ height: `${heightPct}%` }}
              data-testid={`cache-bar-${i}`}
            />
          );
        })}
      </div>
      <div className="flex justify-between px-3 pb-1 text-[9px] text-muted-foreground">
        <span>oldest</span>
        <span>newest →</span>
      </div>
    </div>
  );
}

// ── Pacing trend sparkline (pure SVG — no extra library) ───────────────────

function PacingSparkline({ findings }) {
  const data = findings
    .filter((f) => f.pacing_score != null)
    .sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0));

  if (data.length < 2) return null;

  const W = 400, H = 80, PAD = 12;
  const minCh  = data[0].chapter_number;
  const maxCh  = data[data.length - 1].chapter_number;
  const chSpan = Math.max(1, maxCh - minCh);

  const pts = data.map((d) => {
    const x = PAD + ((d.chapter_number - minCh) / chSpan) * (W - PAD * 2);
    const y = PAD + (1 - d.pacing_score / 100) * (H - PAD * 2);
    return { x, y, ch: d.chapter_number, score: d.pacing_score };
  });

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaD = pathD + ` L ${pts[pts.length - 1].x.toFixed(1)} ${H} L ${pts[0].x.toFixed(1)} ${H} Z`;

  // Reference lines at score 80 (green) and 60 (amber)
  const y80 = PAD + (1 - 80 / 100) * (H - PAD * 2);
  const y60 = PAD + (1 - 60 / 100) * (H - PAD * 2);

  const avgScore = Math.round(data.reduce((s, d) => s + d.pacing_score, 0) / data.length);
  const avgColor = avgScore >= 80 ? '#22c55e' : avgScore >= 60 ? '#f59e0b' : '#f43f5e';

  return (
    <div
      className="rounded-md border border-border/60 overflow-hidden"
      data-testid="pacing-sparkline"
    >
      <div className="flex items-center justify-between border-b border-border/40 bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3 text-muted-foreground" />
          <span className="text-[13px] font-semibold">Pacing Trend</span>
        </div>
        <span
          className="text-[13px] font-bold tabular-nums"
          style={{ color: avgColor }}
        >
          avg {avgScore}
        </span>
      </div>
      <div className="bg-background px-2 pt-1 pb-0.5">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 72 }}
          aria-label="Pacing score trend across chapters"
        >
          {/* Gradient fill under the line */}
          <defs>
            <linearGradient id="sparkg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#sparkg)" />

          {/* Reference lines */}
          <line
            x1={PAD} y1={y80} x2={W - PAD} y2={y80}
            stroke="#22c55e" strokeWidth={0.75} strokeDasharray="3 3" opacity={0.6}
          />
          <text x={W - PAD + 2} y={y80 + 3} fontSize="6" fill="#22c55e" opacity={0.7}>80</text>
          <line
            x1={PAD} y1={y60} x2={W - PAD} y2={y60}
            stroke="#f59e0b" strokeWidth={0.75} strokeDasharray="3 3" opacity={0.6}
          />
          <text x={W - PAD + 2} y={y60 + 3} fontSize="6" fill="#f59e0b" opacity={0.7}>60</text>

          {/* Trend line */}
          <path d={pathD} fill="none" stroke="#6366f1" strokeWidth={1.5} strokeLinejoin="round" />

          {/* Data dots — coloured by score */}
          {pts.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={data.length > 30 ? 1.5 : 3}
              fill={p.score >= 80 ? '#22c55e' : p.score >= 60 ? '#f59e0b' : '#f43f5e'}
              className="cursor-default"
            >
              <title>{`Ch ${p.ch}: ${p.score}`}</title>
            </circle>
          ))}
        </svg>
        <div className="flex justify-between px-0.5 pb-1 text-[9px] text-muted-foreground">
          <span>Ch {minCh}</span>
          <span className="text-primary/50 text-[9px]">pacing score / chapter</span>
          <span>Ch {maxCh}</span>
        </div>
      </div>
    </div>
  );
}

// ── Status pill ────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  done:    'text-emerald-700',
  running: 'text-amber-700',
  queued:  'text-amber-600',
  error:   'text-rose-700',
  stalled: 'text-rose-600',
};

function StatusPill({ status }) {
  const cls = STATUS_COLOR[status] || 'text-muted-foreground';
  return (
    <span className={cn(
      'inline-flex items-center rounded-full border px-2 py-0.5 text-[13px] font-semibold',
      status === 'done'    ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30' :
      status === 'running' ? 'border-amber-300  bg-amber-50  dark:bg-amber-950/30'  :
      status === 'error'   ? 'border-red-300    bg-red-50    dark:bg-red-950/30'    :
                             'border-border      bg-muted/30',
      cls,
    )}>
      {status === 'running' && <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" />}
      {status}
    </span>
  );
}

// ── Chapter finding row ────────────────────────────────────────────────────

function FindingRow({ finding, selected, onToggle, batchMode }) {
  const [open, setOpen] = useState(false);
  const hasPacing = (finding.pacing_issues?.length || 0) > 0;
  const hasIssues = (finding.gaps?.length || 0) + (finding.character_drift?.length || 0) + hasPacing > 0;
  const canSelect = hasPacing || finding.needs_rework;
  const isTimedOut = finding.status === 'error' &&
    (finding.error || '').toLowerCase().includes('timed out');

  return (
    <div className="border-b border-border/30 last:border-0">
      <div className="flex w-full items-center gap-2 px-3 py-2">
        {/* Checkbox — only in batch mode, only for pacing-flagged chapters */}
        {batchMode && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            disabled={!canSelect}
            className="h-3.5 w-3.5 shrink-0 accent-indigo-600 disabled:opacity-30 cursor-pointer"
            data-testid={`batch-fix-checkbox-${finding.chapter_number}`}
            title={canSelect ? 'Select for batch pacing fix' : 'No pacing issues flagged'}
          />
        )}
        {/* Row toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-testid={`cont-finding-row-${finding.chapter_number}`}
        >
          <span className="min-w-[28px] text-[13px] font-mono text-muted-foreground">Ch {finding.chapter_number}</span>
          <span className="flex-1 truncate text-[13.5px] font-medium">{finding.title || `Chapter ${finding.chapter_number}`}</span>
          {finding.status === 'error' && (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-500" title={finding.error} />
          )}
          {finding.needs_rework && finding.status !== 'reworked' && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          )}
          {!hasIssues && finding.status !== 'error' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
          {finding.pacing_score != null && (
            <span className={cn(
              'text-[13px] font-semibold tabular-nums shrink-0',
              finding.pacing_score >= 80 ? 'text-emerald-600' :
              finding.pacing_score >= 60 ? 'text-amber-600' : 'text-rose-600',
            )}>
              {finding.pacing_score}
            </span>
          )}
          {open ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        </button>
      </div>

      {open && (
        <div className="px-3 pb-3 text-[13px] space-y-2">
          {/* Timeout / generic error label */}
          {finding.status === 'error' && (
            <div className="flex items-start gap-1.5 rounded bg-rose-50 dark:bg-rose-950/30 px-2 py-1.5 text-[12px] text-rose-700 dark:text-rose-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {isTimedOut
                ? 'This chapter timed out during review (it may be very long). Resume the walk above to retry — the walker will skip it automatically if it times out again.'
                : (finding.error || 'Review failed for this chapter.')}
            </div>
          )}
          {finding.verdict && (
            <p className="text-muted-foreground italic">{finding.verdict}</p>
          )}
          {(finding.gaps || []).map((g, i) => (
            <div key={i} className="flex gap-1.5">
              <span className="mt-0.5 shrink-0 text-rose-500">•</span>
              <span className="text-rose-800 dark:text-rose-300">{g}</span>
            </div>
          ))}
          {(finding.character_drift || []).map((d, i) => (
            <div key={i} className="flex gap-1.5">
              <span className="mt-0.5 shrink-0 text-amber-500">•</span>
              <span><strong>{d.character}</strong>: {d.issue}</span>
            </div>
          ))}
          {(finding.pacing_issues || []).map((p, i) => (
            <div key={i} className="flex gap-1.5">
              <span className="mt-0.5 shrink-0 text-blue-500">•</span>
              <span className="text-blue-800 dark:text-blue-300">{typeof p === 'string' ? p : p.issue}</span>
            </div>
          ))}
          {!hasIssues && finding.status !== 'error' && (
            <p className="text-emerald-700 dark:text-emerald-400">No issues found in this chapter.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main drawer ────────────────────────────────────────────────────────────

export default function ContinuityReviewDrawer({ open, onClose, project }) {
  const { can, upgradeCta } = useCapabilities();
  const canReview = can('full_novel_review');
  const cta = upgradeCta('full_novel_review');
  const { sessionSaved, hitCount } = useCacheSavings(open);

  const [data, setData]       = useState(null);
  const [busy, setBusy]       = useState(false);
  // Batch pacing fix state
  const [batchMode, setBatchMode]       = useState(false);
  const [selected, setSelected]         = useState(new Set());   // chapter IDs
  const [batchJobId, setBatchJobId]     = useState(null);
  const [batchJob, setBatchJob]         = useState(null);
  const batchPollRef = useRef(null);

  const load = useCallback(async () => {
    if (!project?.id) return;
    try {
      const r = await userContinuityApi.get(project.id);
      setData(r);
    } catch { /* silently ignore */ }
  }, [project?.id]);

  useEffect(() => { if (open) { load(); setBatchMode(false); setSelected(new Set()); } }, [open, load]);

  // Poll walk while running
  useEffect(() => {
    if (!open) return undefined;
    const status = data?.job?.status;
    if (status !== 'running' && status !== 'queued') return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [open, data?.job?.status, load]);

  // Poll batch fix while running
  useEffect(() => {
    if (!batchJobId || !project?.id) return undefined;
    const pollBatch = async () => {
      try {
        const r = await userContinuityApi.getBatchStatus(project.id, batchJobId);
        setBatchJob(r.job);
        if (r.job?.status === 'done' || r.job?.status === 'error') {
          clearInterval(batchPollRef.current);
          batchPollRef.current = null;
          if (r.job.status === 'done') {
            const n = r.job.results?.filter((x) => x.status === 'done').length || 0;
            toast.success(`Batch pacing fix complete — ${n} chapter${n === 1 ? '' : 's'} rewritten.`);
          }
        }
      } catch { /* ignore */ }
    };
    batchPollRef.current = setInterval(pollBatch, 5000);
    pollBatch();
    return () => { if (batchPollRef.current) clearInterval(batchPollRef.current); };
  }, [batchJobId, project?.id]);

  const startReview = async () => {
    setBusy(true);
    try {
      await userContinuityApi.start(project.id);
      toast.success('Full novel review started — this may take several minutes.');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not start review.');
    } finally { setBusy(false); }
  };

  const resumeReview = async () => {
    setBusy(true);
    try {
      const r = await userContinuityApi.resume(project.id);
      toast.success(
        r?.resume_after_chapter != null
          ? `Resuming from chapter ${r.resume_after_chapter + 1}…`
          : 'Review resumed.',
      );
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not resume review.');
    } finally { setBusy(false); }
  };

  const startBatchFix = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await userContinuityApi.batchFix(project.id, [...selected]);
      setBatchJobId(r.job.id);
      setBatchJob(r.job);
      toast.success(`Queued pacing fix for ${selected.size} chapter${selected.size === 1 ? '' : 's'}.`);
      setBatchMode(false);
      setSelected(new Set());
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not start batch fix.');
    } finally { setBusy(false); }
  };

  const toggleSelect = (finding) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key  = finding.chapter_id || finding.id;
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllPacing = () => {
    const keys = (data?.findings || [])
      .filter((f) => (f.pacing_issues?.length || 0) > 0 || f.needs_rework)
      .map((f) => f.chapter_id || f.id)
      .filter(Boolean);
    setSelected(new Set(keys));
  };

  const job         = data?.job;
  const findings    = data?.findings || [];
  const needsRework = data?.needs_rework_count || 0;
  const isRunning   = job?.status === 'running' || job?.status === 'queued';
  const pct = job?.total_chapters
    ? Math.round(((job.chapters_done || 0) / job.total_chapters) * 100) : 0;

  const pacingFlagged = findings.filter((f) => (f.pacing_issues?.length || 0) > 0 || f.needs_rework);
  const isBatchRunning = batchJob?.status === 'running' || batchJob?.status === 'queued';
  const batchPct = batchJob?.total
    ? Math.round(((batchJob.chapters_done || 0) / batchJob.total) * 100) : 0;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-[500px] overflow-y-auto p-0"
        data-testid="continuity-review-drawer"
      >
        {/* Header */}
        <SheetHeader className="border-b border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <SheetTitle className="text-[14px]">Full Novel Review</SheetTitle>
            </div>
            <button
              type="button"
              onClick={load}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            Sequentially reviews every chapter for plot gaps, character drift, and pacing.
          </p>
        </SheetHeader>

        <div className="px-4 py-4 space-y-4">
          {/* Cache savings ribbon — only visible when native-route cache hits occurred */}
          {sessionSaved > 0.0001 && (
            <div
              className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 dark:border-emerald-800/40 dark:bg-emerald-950/20 px-3 py-2"
              data-testid="cache-savings-ribbon"
            >
              <Coins className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="text-[13px] text-emerald-800 dark:text-emerald-300">
                <span className="font-semibold">${sessionSaved.toFixed(4)}</span> saved this session
                {hitCount > 0 && (
                  <span className="ml-1.5 text-emerald-600 dark:text-emerald-500">
                    ({hitCount} cache hit{hitCount === 1 ? '' : 's'})
                  </span>
                )}
                <span className="ml-1.5 text-emerald-600/70 dark:text-emerald-600">
                  · 1-hour Anthropic cache active
                </span>
              </span>
            </div>
          )}
          {/* Upgrade gate */}
          {!canReview && (
            <div className="rounded-lg border border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20 p-4 text-center space-y-2">
              <Lock className="mx-auto h-5 w-5 text-amber-600" />
              <p className="text-[14px] font-semibold text-amber-900 dark:text-amber-300">{cta.title}</p>
              <p className="text-[13px] text-amber-700 dark:text-amber-400">{cta.body}</p>
            </div>
          )}

          {/* Start button */}
          {canReview && (
            <Button
              onClick={startReview}
              disabled={busy || isRunning}
              className="w-full gap-2"
              data-testid="cont-review-start-btn"
            >
              {busy || isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              {isRunning ? 'Review in progress…' : 'Run Full Novel Review'}
            </Button>
          )}

          {/* ── Resume banner — shows when walk is stalled or errored ──── */}
          {canReview && (job?.status === 'stalled' || job?.status === 'error') && (
            <div
              className="flex items-center justify-between gap-3 rounded-lg border border-amber-400/50 bg-amber-50 p-3 dark:border-amber-700/40 dark:bg-amber-950/30"
              data-testid="cont-review-resume-banner"
            >
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold text-amber-900 dark:text-amber-200">
                  Walk paused — {job.chapters_done ?? 0} of {job.total_chapters ?? '?'} chapters reviewed
                </p>
                <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                  Resume restarts from chapter {(job.chapters_done ?? 0) + 1}. Already-reviewed chapters are kept and won't be re-billed.
                </p>
              </div>
              <Button
                size="sm"
                className="shrink-0 bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
                disabled={busy}
                onClick={resumeReview}
                data-testid="cont-review-resume-btn"
              >
                {busy
                  ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
                Resume Walk
              </Button>
            </div>
          )}

          {/* Walk progress bar */}
          {isRunning && (
            <div className="space-y-1">
              <div className="flex justify-between text-[13px] text-muted-foreground">
                <span>Chapter {job.chapters_done || 0} of {job.total_chapters || '?'}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {/* Batch fix active progress */}
          {isBatchRunning && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-indigo-800">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Batch pacing fix in progress…
              </div>
              <div className="space-y-0.5">
                <div className="flex justify-between text-[13px] text-indigo-600">
                  <span>{batchJob.chapters_done || 0} of {batchJob.total || '?'} chapters</span>
                  <span>{batchPct}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-indigo-100 overflow-hidden">
                  <div className="h-full rounded-full bg-indigo-500 transition-all duration-500" style={{ width: `${batchPct}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Batch fix done summary */}
          {batchJob?.status === 'done' && !isBatchRunning && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-emerald-800">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Batch fix complete
              </div>
              <div className="space-y-0.5">
                {(batchJob.results || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[13px]">
                    {r.status === 'done'
                      ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
                      : <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />}
                    <span className="font-medium">Ch {r.number}</span>
                    <span className="text-muted-foreground truncate">{r.summary || r.error || r.reason}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setBatchJob(null)}
                className="text-[13px] text-muted-foreground hover:text-foreground"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Last run summary */}
          {job && (
            <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold">Last run</span>
                <StatusPill status={job.status} />
              </div>
              {job.started_at && (
                <div className="flex items-center gap-1 text-[13px] text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {new Date(job.started_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              {job.status === 'done' && (
                <div className="flex gap-3 text-[13px] pt-0.5">
                  <span className={needsRework > 0 ? 'text-amber-700 font-semibold' : 'text-emerald-700'}>
                    {needsRework > 0 ? `${needsRework} chapter${needsRework === 1 ? '' : 's'} flagged` : 'All clear'}
                  </span>
                  <span className="text-muted-foreground">{job.chapters_done || 0} chapters reviewed</span>
                </div>
              )}
              {job.status === 'error' && job.error && (
                <p className="text-[13px] text-rose-700">{job.error}</p>
              )}
            </div>
          )}

          {/* Pacing sparkline */}
          {findings.length >= 2 && <PacingSparkline findings={findings} />}

          {/* Chapter findings */}
          {findings.length > 0 && (
            <div className="rounded-md border border-border/60 overflow-hidden">
              {/* Section header with batch-fix toggle */}
              <div className="flex items-center justify-between border-b border-border/60 bg-muted/30 px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold">Chapter Findings</span>
                  {needsRework > 0 && (
                    <span className="rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0 text-[9.5px] font-semibold">
                      {needsRework} need attention
                    </span>
                  )}
                </div>
                {/* Batch Fix toggle — only if pacing issues exist */}
                {canReview && pacingFlagged.length > 0 && !isBatchRunning && (
                  <button
                    type="button"
                    onClick={() => {
                      setBatchMode((v) => {
                        if (v) setSelected(new Set());
                        return !v;
                      });
                    }}
                    className={cn(
                      'flex items-center gap-1 rounded px-1.5 py-0.5 text-[13px] font-medium transition-colors',
                      batchMode
                        ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/30',
                    )}
                    data-testid="batch-mode-toggle"
                  >
                    <Wand2 className="h-3 w-3" />
                    {batchMode ? 'Cancel' : 'Batch Fix'}
                  </button>
                )}
              </div>

              {/* Batch selection toolbar */}
              {batchMode && (
                <div className="flex items-center justify-between border-b border-indigo-100 bg-indigo-50/40 px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllPacing}
                      className="text-[13px] text-indigo-600 hover:underline"
                      data-testid="batch-select-all-pacing"
                    >
                      Select all pacing ({pacingFlagged.length})
                    </button>
                    {selected.size > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className="text-[13px] text-muted-foreground hover:underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={startBatchFix}
                    disabled={selected.size === 0 || busy}
                    className="h-6 gap-1 px-2 text-[13px] bg-indigo-600 hover:bg-indigo-700"
                    data-testid="batch-fix-run-btn"
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <TrendingUp className="h-3 w-3" />}
                    Fix {selected.size > 0 ? `${selected.size} ` : ''}Selected
                  </Button>
                </div>
              )}

              {/* Findings list */}
              {findings.map((f) => (
                <FindingRow
                  key={f.id || f.chapter_number}
                  finding={f}
                  batchMode={batchMode}
                  selected={selected.has(f.chapter_id || f.id)}
                  onToggle={() => toggleSelect(f)}
                />
              ))}
            </div>
          )}

          {!job && canReview && (
            <p className="text-center text-[13.5px] text-muted-foreground py-6">
              No review has been run yet. Click "Run Full Novel Review" to start.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
